/**
 * Mock Data for Testing Emergency Plan Pipeline
 * 
 * Comprehensive test data representing a complex Los Angeles household
 * with multiple special needs, pets, and 12 emergency types.
 */

export const mockInterview = {
  name: "Sarah Chen",
  email: "test.user@example.com",
  location: {
    address: "1234 Hillside Drive",
    city: "Los Angeles",
    state: "CA",
    county: "Los Angeles County",
    country: "United States",
    zip: "90210"
  },
  household_size: 5,
  household_ages: [72, 45, 42, 16, 8],
  household_members: [
    { name: "Margaret Chen", age: 72, role: "Grandmother", medical: ["diabetes", "mobility aid"], skills: [] },
    { name: "David Chen", age: 45, role: "Father", medical: [], skills: ["CPR certified", "First Aid"] },
    { name: "Sarah Chen", age: 42, role: "Mother", medical: ["asthma"], skills: ["Nurse - RN"] },
    { name: "Emily Chen", age: 16, role: "Daughter", medical: [], skills: ["Babysitting", "Swimming"] },
    { name: "Lucas Chen", age: 8, role: "Son", medical: ["peanut allergy"], skills: [] }
  ],
  pets: [
    { type: "Dog", name: "Max", breed: "Golden Retriever", special_needs: "Anxiety during storms" },
    { type: "Cat", name: "Whiskers", breed: "Tabby", special_needs: null }
  ],
  property_type: "House",
  property_ownership: "Own",
  property_details: {
    stories: 2,
    basement: false,
    garage: true,
    construction_type: "wood",
    year_built: 1985,
    utility_shutoffs: {
      gas: "Right side of house near meter",
      water: "Front yard near sidewalk",
      electric: "Garage panel"
    }
  },
  emergency_types: [
    "Earthquake",
    "Wildfire", 
    "Flood",
    "Power Outage",
    "Extreme Heat",
    "Landslide",
    "Tsunami",
    "Chemical Spill",
    "Home Fire",
    "Medical Emergency",
    "Civil Unrest",
    "Pandemic"
  ],
  medical_needs: {
    conditions: ["Type 2 Diabetes", "Asthma", "Severe Peanut Allergy"],
    medications: [
      { name: "Metformin", person: "Margaret", refill_date: "Monthly" },
      { name: "Albuterol Inhaler", person: "Sarah", refill_date: "As needed" },
      { name: "EpiPen", person: "Lucas", refill_date: "Annual" }
    ],
    equipment: ["Blood glucose monitor", "Nebulizer"],
    medication_supply: "30 days"
  },
  special_needs: ["Elderly mobility assistance", "Child with severe allergy"],
  budget: "$200-500",
  ai_insights: {
    primary_fear: "Wildfire evacuation with elderly mother",
    mobility_concerns: "Grandmother uses walker, needs 10+ minutes for stairs",
    medical_dependencies: "Insulin needs refrigeration, backup ice packs required",
    pet_evacuation_challenges: "Dog anxious, won't crate easily",
    hidden_resources: "Sarah is RN - can triage, David CPR certified",
    communication_plan_gaps: "Kids at different schools, no meeting point established",
    budget_constraints: "Can spend $300 this month, already have some supplies",
    emotional_readiness: "Motivated by recent wildfires in area"
  },
  nuanced_details: {
    past_experience: "Evacuated during 2020 Bobcat Fire, felt unprepared",
    neighborhood_network: "Know 3 neighbors well, one has generator",
    work_schedules: "David works from home, Sarah 12-hour hospital shifts",
    school_locations: ["Lincoln High (Emily)", "Washington Elementary (Lucas)"],
    vehicle_situation: "2 cars, one SUV can fit all + pets"
  },
  phase_completed: 2,
  status: "completed"
};

export const mockResearch = {
  government_resources: {
    fema_region: "Region IX",
    state_agency: "Cal OES (California Office of Emergency Services)",
    local_ema: "Los Angeles County Office of Emergency Management",
    alert_systems: ["AlertLA County", "Wireless Emergency Alerts", "NOAA Weather Radio"],
    official_guides: ["ready.gov", "ready.ca.gov", "ready.lacounty.gov"]
  },
  historical_disasters: {
    significant_events: [
      { year: 2020, type: "Wildfire", name: "Bobcat Fire", damage: "$22M", evacuees: 7000 },
      { year: 2019, type: "Earthquake", name: "Ridgecrest", magnitude: 7.1 },
      { year: 2018, type: "Wildfire", name: "Woolsey Fire", damage: "$6B", fatalities: 3 },
      { year: 1994, type: "Earthquake", name: "Northridge", magnitude: 6.7, damage: "$44B" }
    ],
    fema_declarations: 45,
    primary_hazards: ["Earthquake", "Wildfire", "Flood", "Extreme Heat"]
  },
  hazard_zones: {
    earthquake: {
      nearest_fault: "Hollywood Fault - 2.3 miles",
      soil_type: "Alluvial - moderate liquefaction risk",
      seismic_zone: "Zone 4 (highest)"
    },
    wildfire: {
      fire_hazard_zone: "Very High Fire Hazard Severity Zone",
      wui_status: "Wildland-Urban Interface",
      cal_fire_rating: "High"
    },
    flood: {
      fema_zone: "Zone X (minimal flood risk)",
      nearest_waterway: "LA River - 4.2 miles"
    }
  },
  emergency_services: {
    hospitals: [
      { name: "Cedars-Sinai Medical Center", distance: "3.2 mi", trauma_level: "Level I", er_phone: "(310) 423-8780" },
      { name: "UCLA Medical Center", distance: "4.8 mi", trauma_level: "Level I", er_phone: "(310) 825-9111" }
    ],
    fire_stations: [
      { name: "LAFD Station 27", distance: "0.8 mi", response_time: "4 min" }
    ],
    shelters: [
      { name: "Beverly Hills High School", capacity: 500, pet_friendly: true, distance: "1.5 mi" },
      { name: "Robertson Recreation Center", capacity: 200, pet_friendly: false, distance: "2.1 mi" }
    ],
    utilities: {
      electric: { provider: "SoCal Edison", outage_line: "1-800-611-1911" },
      gas: { provider: "SoCal Gas", emergency: "1-800-427-2200" },
      water: { provider: "LADWP", emergency: "1-800-342-5397" }
    }
  },
  community_resources: {
    red_cross: { chapter: "American Red Cross of Los Angeles", phone: "(213) 739-5200" },
    cert_program: { location: "Beverly Hills CERT", next_training: "February 2026" },
    supply_stores: [
      { name: "REI", distance: "3.1 mi", has_emergency_supplies: true },
      { name: "Big 5 Sporting Goods", distance: "1.8 mi" }
    ],
    food_banks: [
      { name: "Los Angeles Regional Food Bank", phone: "(323) 234-3030" }
    ]
  },
  special_needs_resources: {
    dialysis_centers: [
      { name: "DaVita Beverly Hills", distance: "2.1 mi", phone: "(310) 274-1234" }
    ],
    home_health: [
      { name: "Maxim Healthcare", services: ["Nursing", "Medical Equipment"], phone: "(310) 555-0123" }
    ],
    accessible_transport: [
      { name: "Access Services", phone: "1-800-827-0829" }
    ]
  },
  pet_resources: {
    emergency_vets: [
      { name: "VCA West LA Animal Hospital", address: "1900 S Sepulveda Blvd", phone: "(310) 473-2951", hours: "24/7" }
    ],
    pet_friendly_shelters: ["Beverly Hills High School Shelter"],
    boarding_facilities: [
      { name: "Camp Bow Wow", phone: "(310) 555-7890", accepts_emergency: true }
    ]
  },
  recovery_resources: {
    fema_assistance: {
      iag_phone: "1-800-621-3362",
      disasterassistance_gov: true,
      sba_disaster_loans: true
    },
    insurance_info: {
      state_commissioner: "CA Dept of Insurance",
      consumer_hotline: "1-800-927-4357"
    },
    mental_health: [
      { name: "Disaster Distress Helpline", phone: "1-800-985-5990" },
      { name: "SAMHSA", phone: "1-800-662-4357" }
    ],
    debris_removal: {
      la_county_program: "Public Assistance Program",
      contact: "LA County OES"
    }
  }
};

export type MockInterview = typeof mockInterview;
export type MockResearch = typeof mockResearch;
